import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { Phone, Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { CONTACT, FAQS, MARQUEE, SERVICES, AUDIENCES, STEPS, SOFT_LANDING, PARTNERS, INTRO } from "../content";
import { Check } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

export const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
    window.__lenis = lenis;
    let id;
    const raf = (t) => { lenis.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); };
  }, []);
};

export const LogoMark = ({ className = "w-11 h-11" }) => (
  <img src="/images/logo.png" alt="Inner You Development" className={`${className} rounded-full object-cover`} />
);

export const RevealLines = ({ lines, className = "", emIndex = -1, emClass = "italic", delay = 0.2 }) => (
  <h1 className={className}>
    {lines.map((l, i) => (
      <span key={l} className="block overflow-hidden pb-[0.08em]">
        <motion.span
          className={`block ${i === emIndex ? emClass : ""}`}
          initial={{ y: "105%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1.1, ease, delay: delay + i * 0.14 }}
        >
          {l}
        </motion.span>
      </span>
    ))}
  </h1>
);

export const Reveal = ({ children, className = "", delay = 0, y = 28 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.12 }}
    transition={{ duration: 0.9, ease, delay }}
  >
    {children}
  </motion.div>
);

export const Marquee = ({ className = "", itemClass = "", dotClass = "" }) => {
  const items = [...MARQUEE, ...MARQUEE];
  return (
    <div className={`overflow-hidden whitespace-nowrap ${className}`} data-testid="marquee">
      <div className="marquee-track inline-flex">
        {[0, 1].map((k) => (
          <div key={k} className="inline-flex items-center" aria-hidden={k === 1}>
            {items.map((w, i) => (
              <span key={`${k}-${i}`} className="inline-flex items-center">
                <span className={itemClass}>{w}</span>
                <span className={`mx-8 sm:mx-12 ${dotClass}`}>✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const Faq = ({ itemClass = "", triggerClass = "", contentClass = "" }) => (
  <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
    {FAQS.map((f, i) => (
      <AccordionItem key={f.q} value={`f${i}`} className={itemClass}>
        <AccordionTrigger data-testid={`faq-trigger-${i}`} className={`text-left hover:no-underline ${triggerClass}`}>
          {f.q}
        </AccordionTrigger>
        <AccordionContent className={contentClass}>{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const ContactLinks = ({ className = "", linkClass = "", prefix = "contact" }) => (
  <div className={className}>
    <a href={CONTACT.phoneHref} data-testid={`${prefix}-phone-link`} className={`group inline-flex items-center gap-3 ${linkClass}`}>
      <Phone className="w-4 h-4 shrink-0" strokeWidth={1.5} /> <span>{CONTACT.phone}</span>
    </a>
    <a href={CONTACT.emailHref} data-testid={`${prefix}-email-link`} className={`group inline-flex items-center gap-3 break-all ${linkClass}`}>
      <Mail className="w-4 h-4 shrink-0" strokeWidth={1.5} /> <span>{CONTACT.email}</span>
    </a>
  </div>
);

export const ServiceTiles = ({ className = "", tileClass = "", titleClass = "", textClass = "", iconClass = "" }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ${className}`}>
    {SERVICES.map((s, i) => (
      <Reveal key={s.title} delay={(i % 4) * 0.07}>
        <a href={CONTACT.phoneHref} data-testid={`service-card-${i}`} className={`group flex h-full gap-4 p-6 transition-[transform,background-color] duration-500 hover:-translate-y-1 ${tileClass}`}>
          <s.icon className={`w-7 h-7 shrink-0 transition-transform duration-500 group-hover:scale-110 ${iconClass}`} strokeWidth={1.2} />
          <div><h3 className={titleClass}>{s.title}</h3><p className={`mt-1 ${textClass}`}>{s.text}</p></div>
        </a>
      </Reveal>
    ))}
  </div>
);

export const Audiences = ({ className = "", circleClass = "", labelClass = "" }) => (
  <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 ${className}`} data-testid="audiences">
    {AUDIENCES.map((a, i) => (
      <Reveal key={a.label} delay={i * 0.07} className="flex flex-col items-center text-center">
        <span className={`w-16 h-16 rounded-full grid place-items-center ${circleClass}`}><a.icon className="w-6 h-6" strokeWidth={1.3} /></span>
        <p className={`mt-4 max-w-[10rem] ${labelClass}`}>{a.label}</p>
      </Reveal>
    ))}
  </div>
);

export const Steps = ({ className = "", circleClass = "", numClass = "", titleClass = "", textClass = "", lineClass = "" }) => (
  <ol className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6 ${className}`} data-testid="referral-steps">
    {STEPS.map((s, i) => (
      <Reveal key={s.title} delay={i * 0.1} className="relative">
        <li className="list-none">
          {i < STEPS.length - 1 && <span className={`hidden lg:block absolute top-8 left-24 right-2 h-px ${lineClass}`} />}
          <span className={`relative w-16 h-16 rounded-full grid place-items-center ${circleClass}`}>
            <s.icon className="w-6 h-6" strokeWidth={1.3} />
            <span className={`absolute -top-1 -left-1 w-6 h-6 rounded-full grid place-items-center text-[11px] ${numClass}`}>{i + 1}</span>
          </span>
          <h3 className={`mt-6 ${titleClass}`}>{s.title}</h3>
          <p className={`mt-2 ${textClass}`}>{s.text}</p>
        </li>
      </Reveal>
    ))}
  </ol>
);

export const SoftLanding = ({ serif = "font-newsreader", sans = "font-jakarta", className = "" }) => (
  <section className={`py-24 sm:py-32 ${className}`} data-testid="soft-landing-section">
    <div className="max-w-[1300px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-5">
        <p className={`${sans} text-xs uppercase tracking-[0.3em] text-[#5E6B57]`}>Our soft landing approach</p>
        <h2 className={`mt-6 ${serif} text-4xl sm:text-5xl leading-[1.05] text-[#3F4A3C]`}>{SOFT_LANDING.title}</h2>
        <p className={`mt-6 ${sans} text-base sm:text-lg leading-relaxed text-[#4A4F45]`}>{SOFT_LANDING.body}</p>
        <a href={CONTACT.phoneHref} data-testid="soft-landing-call" className={`mt-8 inline-flex rounded-full bg-[#3F4A3C] px-7 py-4 ${sans} text-sm text-[#F6F2E9] hover:bg-[#5F7C8A] transition-colors duration-500`}>Happy to have a chat: {CONTACT.phone}</a>
      </Reveal>
      <div className="lg:col-span-7 grid gap-5">
        <div className="grid sm:grid-cols-2 gap-5">
          {SOFT_LANDING.promises.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1} className="rounded-[28px] bg-[#E3E9E4] p-8">
              <h3 className={`${serif} text-2xl text-[#3F4A3C]`}>{p.title}</h3>
              <p className={`mt-3 ${sans} text-sm sm:text-base leading-relaxed text-[#4A4F45]`}>{p.text}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="rounded-[28px] bg-[#DCE6EC] p-8">
          <h3 className={`${serif} text-2xl text-[#3F4A3C]`}>How we make it easier</h3>
          <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {SOFT_LANDING.ease.map((e) => (
              <li key={e} className={`flex gap-3 ${sans} text-sm text-[#4A4F45]`}><Check className="w-4 h-4 mt-0.5 shrink-0 text-[#5F7C8A]" />{e}</li>
            ))}
          </ul>
        </Reveal>
      </div>
    </div>
  </section>
);

export const Partners = ({ serif = "font-newsreader", sans = "font-jakarta", className = "" }) => {
  const [tab, setTab] = useState(PARTNERS[0].key);
  const active = PARTNERS.find((p) => p.key === tab);
  return (
    <section id="partners" className={`py-24 sm:py-32 ${className}`} data-testid="partners-section">
      <div className="max-w-[1300px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <p className={`${sans} text-xs uppercase tracking-[0.3em] text-[#5E6B57]`}>For referrers & partners</p>
          <h2 className={`mt-6 ${serif} text-4xl sm:text-5xl leading-[1.05] text-[#3F4A3C]`}>Let's make the right support easier to reach.</h2>
          <p className={`mt-6 ${sans} text-base leading-relaxed text-[#4A4F45]`}>{INTRO}</p>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <div className="flex flex-wrap gap-2" role="tablist">
            {PARTNERS.map((p) => (
              <button key={p.key} role="tab" aria-selected={tab === p.key} onClick={() => setTab(p.key)} data-testid={`partner-tab-${p.key}`}
                className={`rounded-full px-4 py-2 ${sans} text-sm transition-colors duration-300 ${tab === p.key ? "bg-[#3F4A3C] text-[#F6F2E9]" : "bg-[#E3E9E4] text-[#3F4A3C] hover:bg-[#DCE6EC]"}`}>{p.label}</button>
            ))}
          </div>
          <div className="mt-6 rounded-[28px] border border-[#3F4A3C]/15 p-8 sm:p-10" data-testid="partner-panel">
            <motion.p key={active.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className={`${serif} text-2xl sm:text-3xl leading-snug text-[#3F4A3C]`}>{active.text}</motion.p>
            <div className={`mt-8 flex flex-wrap gap-4 ${sans} text-sm`}>
              <a href={`${CONTACT.emailHref}?subject=${encodeURIComponent(`Connecting with IYD (${active.label})`)}`} data-testid="partner-email-btn" className="rounded-full bg-[#3F4A3C] px-6 py-3 text-[#F6F2E9] hover:bg-[#5F7C8A] transition-colors">Email our team</a>
              <a href={CONTACT.phoneHref} data-testid="partner-call-btn" className="rounded-full border border-[#3F4A3C]/25 px-6 py-3 text-[#3F4A3C] hover:bg-[#E3E9E4] transition-colors">Give us a call</a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -90 }) : el.scrollIntoView();
      }, 350);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [pathname, hash]);
  return null;
};

